import React, { useEffect, useRef, useState } from 'react';
import DayPicker, { Modifier } from 'react-day-picker';
import classNames from 'classnames';
import DateTimeParser from '../../utils/DateTimeParser';
import MomentLocaleUtils from 'react-day-picker/moment';
import DateFieldYearMonthForm from './components/DateFieldYearMonthForm';
import DateFieldNavbar from './components/DateFieldNavbar';
import styles from './DateField.module.scss';
import 'react-day-picker/lib/style.css';

type PropsType = {
  name: string;
  disabledDays?: Modifier | Modifier[];
  navbar?: boolean;
  value?: string;
  disabled?: boolean;
  error?: string;
  locale?: string;
  validate?: (value: string) => string;
  onChange?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void;
};

const DateField = (props: PropsType) => {
  const currentYear = new Date().getFullYear();
  const fromMonth = new Date(currentYear - 140, 0);
  const toMonth = new Date(currentYear, 0);
  const dayPickerWrapperRef = useRef<HTMLDivElement>(null);
  const dayPickerRef = useRef<HTMLDivElement>(null);
  const [isShowingDayPicker, setIsShowingDayPicker] = useState<boolean>(false);
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(
    props.value ? DateTimeParser.toDateObject(props.value) : undefined
  );

  const handleYearMonthChange = (month: Date) => {
    setSelectedDay(month);
  };

  const handleOnDayClick = (date: Date) => {
    const dateValueString = DateTimeParser.toLocaleDateString(date);

    setIsShowingDayPicker(false);
    setSelectedDay(date);

    if (props.onChange) {
      const handleChangeEvent = {
        target: {
          id: props.name,
          name: props.name,
          value: dateValueString,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      props.onChange(dateValueString, handleChangeEvent);
    }
  };

  const handleClick = (e: MouseEvent) => {
    if (dayPickerWrapperRef.current && dayPickerWrapperRef.current.contains(e.target as Node)) {
      if (!(dayPickerRef.current && dayPickerRef.current.contains(e.target as Node))) {
        setIsShowingDayPicker((prev) => !prev);
      }
    } else {
      setIsShowingDayPicker(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleClick);

    return () => {
      document.removeEventListener('mouseup', handleClick);
    };
  });

  return (
    <>
      <div ref={dayPickerWrapperRef} className={styles.wrapper}>
        <input
          type="text"
          name={props.name}
          value={props.value}
          className={classNames(styles.input, props.disabled && styles.disabled)}
          disabled={props.disabled}
          readOnly
          aria-label={props.name}
        />

        <div className={styles.errorField}>{props.error && <div>{props.error}</div>}</div>

        {isShowingDayPicker && (
          <div ref={dayPickerRef} className={styles.dayPicker}>
            <DayPicker
              month={selectedDay}
              modifiers={{
                disabled: props.disabledDays,
              }}
              fromMonth={props.navbar ? fromMonth : undefined}
              toMonth={props.navbar ? toMonth : undefined}
              selectedDays={selectedDay}
              locale={props.locale || DateTimeParser.getCurrentCultureCode() || 'en'}
              localeUtils={MomentLocaleUtils}
              captionElement={
                props.navbar
                  ? ({ date, localeUtils }: { date: any; localeUtils: any }) => (
                      <DateFieldYearMonthForm
                        fromMonth={fromMonth}
                        toMonth={toMonth}
                        date={date}
                        localeUtils={localeUtils}
                        onChange={handleYearMonthChange}
                      />
                    )
                  : undefined
              }
              navbarElement={
                props.navbar ? (props: any) => <DateFieldNavbar {...props} /> : undefined
              }
              onDayClick={handleOnDayClick}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DateField;
